/**
 * Buyer Start measuring presentation + Phase 2B activation client.
 * Creates no Case endpoint. Enablement uses existing schedule APIs.
 */

import type { Policy } from '../../services/policiesApi';
import type {
  ScanDefinition,
  ScanDefinitionWriteInput,
  ScheduleWriteInput,
  ScheduledScanSchedule,
} from '../../services/scheduledScansApi';
import { OPEN_SAFETY_CASE_LABEL } from './managedSafetyBuyerUx';

export const START_MEASURING_LABEL = 'Start measuring';
export const SAVE_FOR_LATER_LABEL = 'Save for later';
export const RESUME_MEASURING_LABEL = 'Resume measuring';
export const MEASUREMENT_ACTIVE_LABEL = 'Measurement active';
export const FIRST_RESULT_PENDING_LABEL = 'First result pending';
export const MONITORING_ACTIVE_COPY = 'Monitoring';
export const START_MEASURING_FAILURE = 'Could not start measuring. Try again.';
export const MISSING_CAMERA_MESSAGE = 'Camera required';
export const MISSING_AREA_MESSAGE = 'Area/Zone required';
export const POLICY_INCOMPLETE_MESSAGE = 'policy incomplete';

export const CREATE_SCHEDULE_PATH = '/api/v1/sites/:siteId/scan-definitions/:definitionId/schedules';
export const UPDATE_SCHEDULE_PATH = '/api/v1/sites/:siteId/schedules/:scheduleId';

const TECHNICAL_ERROR_TOKENS = [
  'workflow_id',
  'engine_config',
  'workflow_context',
  'technical_anchor',
  'finding_id',
  'eligibility',
  'baseline epoch',
  'case admission',
  'resolver',
  'schedule row',
];

const WHOLE_VIEW_POLICY_TYPES = new Set([
  'ppe_compliance',
  'occupancy',
  'worker_down',
]);

export type StartMeasuringMode = 'start' | 'resume' | 'active' | 'blocked';

export type StartMeasuringPresentation = {
  mode: StartMeasuringMode;
  primaryLabel: string;
  secondaryLabel: string | null;
  statusLabel: string | null;
  openCaseLabel: string | null;
  missingMessage: string | null;
};

export type StartMeasuringPolicyInput = {
  policy_id?: string | null;
  policy_type?: string | null;
  name?: string | null;
  camera_id?: string | null;
  edge_device_id?: string | null;
  target?: { camera_id?: string | null; edge_device_id?: string | null } | null;
  scope?: {
    camera_ids?: string[] | null;
    zone_ids?: string[] | null;
    edge_device_id?: string | null;
    geometry_mode?: string | null;
  } | null;
  config?: Record<string, unknown> | null;
  status?: string | null;
};

export type StartMeasuringApi = {
  listDefinitions: (siteId: string) => Promise<ScanDefinition[]>;
  createDefinition: (siteId: string, input: ScanDefinitionWriteInput) => Promise<ScanDefinition>;
  listSchedules: (siteId: string, definitionId: string) => Promise<ScheduledScanSchedule[]>;
  createSchedule: (siteId: string, definitionId: string, input: ScheduleWriteInput) => Promise<ScheduledScanSchedule>;
  updateSchedule: (
    siteId: string,
    scheduleId: string,
    input: ScheduleWriteInput & { expected_version: number },
  ) => Promise<ScheduledScanSchedule>;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => text(item)).filter(Boolean) : [];
}

export function policySaveActivatesMeasurement(): boolean {
  return false;
}

export function readPolicyCameraId(policy: StartMeasuringPolicyInput | Policy | null | undefined): string {
  if (!policy) return '';
  return text(
    policy.camera_id
    || policy.target?.camera_id
    || (Array.isArray(policy.scope?.camera_ids) ? policy.scope?.camera_ids?.[0] : ''),
  );
}

export function readPolicyZoneIds(policy: StartMeasuringPolicyInput | Policy | null | undefined): string[] {
  return strings(policy?.scope?.zone_ids);
}

export function readPolicyLineId(policy: StartMeasuringPolicyInput | Policy | null | undefined): string {
  const config = policy?.config && typeof policy.config === 'object' ? policy.config : {};
  const nested = config.line && typeof config.line === 'object'
    ? (config.line as Record<string, unknown>)
    : {};
  return text(config.line_id || nested.line_id);
}

export function readPolicyDeviceId(policy: StartMeasuringPolicyInput | Policy | null | undefined): string {
  if (!policy) return '';
  return text(policy.edge_device_id || policy.target?.edge_device_id || policy.scope?.edge_device_id);
}

function isWholeView(policy: StartMeasuringPolicyInput | Policy | null | undefined): boolean {
  const type = text(policy?.policy_type);
  const geometry = text(policy?.scope?.geometry_mode).toLowerCase();
  return geometry === 'whole_view' || WHOLE_VIEW_POLICY_TYPES.has(type);
}

export function validateStartMeasuringConfig(
  policy: StartMeasuringPolicyInput | Policy | null | undefined,
): string | null {
  if (!policy || !text(policy.policy_id) || !text(policy.policy_type)) {
    return POLICY_INCOMPLETE_MESSAGE;
  }
  if (!readPolicyCameraId(policy)) return MISSING_CAMERA_MESSAGE;
  if (!readPolicyZoneIds(policy).length && !readPolicyLineId(policy) && !isWholeView(policy)) {
    return MISSING_AREA_MESSAGE;
  }
  return null;
}

export function extractActivatedCaseId(schedule: {
  safety_case_id?: string | null;
  finding_id?: string | null;
} | null | undefined): string {
  return text(schedule?.safety_case_id || schedule?.finding_id);
}

export function caseIdFromDefinition(definition: ScanDefinition | null | undefined): string {
  const context = definition?.engine_config?.workflow_context;
  if (!context || typeof context !== 'object') return '';
  const record = context as Record<string, unknown>;
  return text(record.safety_case_id || record.finding_id);
}

export function mapStartMeasuringError(cause: unknown): string {
  const data = (cause as { response?: { data?: { detail?: string; error?: string; message?: string } } })
    ?.response?.data;
  const raw = text(data?.detail || data?.error || data?.message || (cause as Error)?.message);
  const lower = raw.toLowerCase();
  if (TECHNICAL_ERROR_TOKENS.some((token) => lower.includes(token))) {
    return START_MEASURING_FAILURE;
  }
  if (lower.includes('camera')) return MISSING_CAMERA_MESSAGE;
  if (lower.includes('zone') || lower.includes('area')) return MISSING_AREA_MESSAGE;
  if (lower.includes('invalid_scan_scope') || lower.includes('policy')) return POLICY_INCOMPLETE_MESSAGE;
  return raw && raw.length < 140 ? raw : START_MEASURING_FAILURE;
}

export function presentStartMeasuring(input: {
  policy: StartMeasuringPolicyInput | Policy | null | undefined;
  scheduleEnabled?: boolean | null;
  safetyCaseId?: string | null;
}): StartMeasuringPresentation {
  const missing = validateStartMeasuringConfig(input.policy);
  const caseId = text(input.safetyCaseId);
  if (missing) {
    return {
      mode: 'blocked',
      primaryLabel: START_MEASURING_LABEL,
      secondaryLabel: SAVE_FOR_LATER_LABEL,
      statusLabel: null,
      openCaseLabel: null,
      missingMessage: missing,
    };
  }
  if (caseId && input.scheduleEnabled) {
    return {
      mode: 'active',
      primaryLabel: OPEN_SAFETY_CASE_LABEL,
      secondaryLabel: null,
      statusLabel: MEASUREMENT_ACTIVE_LABEL,
      openCaseLabel: OPEN_SAFETY_CASE_LABEL,
      missingMessage: null,
    };
  }
  if (caseId && input.scheduleEnabled === false) {
    return {
      mode: 'resume',
      primaryLabel: RESUME_MEASURING_LABEL,
      secondaryLabel: SAVE_FOR_LATER_LABEL,
      statusLabel: null,
      openCaseLabel: null,
      missingMessage: null,
    };
  }
  return {
    mode: 'start',
    primaryLabel: START_MEASURING_LABEL,
    secondaryLabel: SAVE_FOR_LATER_LABEL,
    statusLabel: null,
    openCaseLabel: null,
    missingMessage: null,
  };
}

export function presentZeroScanActivationHome(input: {
  scanCount: number;
  reportId?: string | null;
  nextWindowLabel?: string | null;
}): {
  measurementActive: boolean;
  firstResultPending: boolean;
  showOpenSafetyScan: boolean;
  currentScanLabel: string;
  nextWindowLabel: string | null;
} {
  const reportId = text(input.reportId);
  const zeroScan = input.scanCount <= 0;
  return {
    measurementActive: true,
    firstResultPending: zeroScan && !reportId,
    showOpenSafetyScan: Boolean(reportId),
    currentScanLabel: reportId
      ? 'Safety Scan'
      : (zeroScan ? FIRST_RESULT_PENDING_LABEL : 'Safety Scan'),
    nextWindowLabel: text(input.nextWindowLabel) || null,
  };
}

function sameIds(left: string[], right: string[]): boolean {
  return [...left].sort().join('|') === [...right].sort().join('|');
}

export function matchScanDefinitionForPolicy(
  definitions: ScanDefinition[],
  policy: StartMeasuringPolicyInput | Policy,
): ScanDefinition | undefined {
  const policyId = text(policy.policy_id);
  const cameraId = readPolicyCameraId(policy);
  const zoneIds = readPolicyZoneIds(policy);
  const policyType = text(policy.policy_type);
  const active = definitions.filter((item) => item.status === 'ACTIVE');
  const byPolicyId = active.find((item) => strings(item.scope?.policy_ids).includes(policyId));
  if (byPolicyId) return byPolicyId;
  return active.find((item) => {
    const scope = item.scope || {};
    const cameras = strings(scope.camera_ids);
    const zones = strings(scope.zone_ids);
    const type = text(scope.policy_type || scope.incident_type);
    return cameras.includes(cameraId)
      && sameIds(zones, zoneIds)
      && (!policyType || type === policyType);
  });
}

export function buildScanDefinitionInput(
  policy: StartMeasuringPolicyInput | Policy,
): ScanDefinitionWriteInput {
  const cameraId = readPolicyCameraId(policy);
  const zoneIds = readPolicyZoneIds(policy);
  const deviceId = readPolicyDeviceId(policy);
  const policyType = text(policy.policy_type);
  return {
    name: text(policy.name) || 'Safety condition',
    description: 'Measurement for a saved safety policy.',
    scope: {
      camera_ids: cameraId ? [cameraId] : [],
      zone_ids: zoneIds,
      policy_ids: text(policy.policy_id) ? [text(policy.policy_id)] : [],
      ...(deviceId ? { device_id: deviceId } : {}),
      ...(policyType ? { policy_type: policyType, incident_type: policyType } : {}),
    },
  };
}

export function defaultActivationScheduleInput(timezone = 'UTC'): ScheduleWriteInput {
  return {
    enabled: true,
    cadence: 'DAILY',
    day_of_week: null,
    execution_time_local: '09:00',
    timezone: text(timezone) || 'UTC',
    window_length_seconds: 86400,
    settlement_delay_seconds: 300,
    retention_policy_id: 'default',
    recipients: [],
  };
}

export function scheduleWriteInput(
  schedule: ScheduledScanSchedule,
  enabled: boolean,
): ScheduleWriteInput {
  return {
    enabled,
    cadence: schedule.cadence === 'OFF' ? 'DAILY' : schedule.cadence,
    day_of_week: schedule.day_of_week ?? null,
    execution_time_local: String(schedule.execution_time_local || '09:00').slice(0, 5),
    timezone: schedule.timezone || 'UTC',
    window_length_seconds: Number(schedule.window_length_seconds || 86400),
    settlement_delay_seconds: Number(schedule.settlement_delay_seconds || 0),
    retention_policy_id: schedule.retention_policy_id || 'default',
    recipients: schedule.recipients || [],
  };
}

export async function activateMeasurement(args: {
  siteId: string;
  policy: StartMeasuringPolicyInput | Policy;
  timezone?: string | null;
  api: StartMeasuringApi;
}): Promise<{ caseId: string; alreadyActive: boolean; endpoint: 'create' | 'update' | 'existing' }> {
  const missing = validateStartMeasuringConfig(args.policy);
  if (missing) {
    throw new Error(missing);
  }
  const definitions = await args.api.listDefinitions(args.siteId);
  let definition = matchScanDefinitionForPolicy(definitions, args.policy);
  if (!definition) {
    definition = await args.api.createDefinition(args.siteId, buildScanDefinitionInput(args.policy));
  }
  const schedules = await args.api.listSchedules(args.siteId, definition.scan_definition_id);
  const existing = schedules.find((item) => item.enabled) || schedules[0] || null;
  const existingCaseId = extractActivatedCaseId(existing) || caseIdFromDefinition(definition);
  if (existing?.enabled && existingCaseId) {
    return { caseId: existingCaseId, alreadyActive: true, endpoint: 'existing' };
  }
  const next = existing
    ? await args.api.updateSchedule(args.siteId, existing.id, {
        ...scheduleWriteInput(existing, true),
        expected_version: existing.version,
      })
    : await args.api.createSchedule(
        args.siteId,
        definition.scan_definition_id,
        defaultActivationScheduleInput(args.timezone || 'UTC'),
      );
  const caseId = extractActivatedCaseId(next);
  if (!caseId) {
    throw new Error(START_MEASURING_FAILURE);
  }
  return { caseId, alreadyActive: false, endpoint: existing ? 'update' : 'create' };
}
